import { Asset } from '@/api/generatedApi';
import { useResolveDependency } from '@/hooks/api/assets/useResolveDependency';
import { usePopover } from '@/hooks/usePopover';
import { useToast } from '@/hooks/useToast';
import { getFilename } from '@/utils/path';
import React, { useRef, useState } from 'react';

export const useResolveFlow = () => {
  const { showToast } = useToast();
  const { resolveDependency } = useResolveDependency({
    onSuccess: () => {
      showToast({ message: 'Dependency resolved successfully', type: 'success' });
    },
    onError: errorMessage => {
      showToast({ message: errorMessage, type: 'error' });
    },
  });

  const {
    open: openResolvePopover,
    close: closeResolvePopover,
    isOpen: isResolvePopoverOpen,
    anchorElement: resolveAnchorElement,
  } = usePopover();

  const [pendingResolvePath, setPendingResolvePath] = useState('');
  const pendingAssemblyIdReference = useRef('');
  const [isAssetSelectionOpen, setIsAssetSelectionOpen] = useState(false);
  const [pendingResolveAsset, setPendingResolveAsset] = useState<Asset | undefined>();
  const [resolveConfirmOpen, setResolveConfirmOpen] = useState(false);
  const [isSwitchMode, setIsSwitchMode] = useState(false);
  const [currentResolvedName, setCurrentResolvedName] = useState<string | undefined>();

  const handleResolveClick = (event: React.MouseEvent<HTMLButtonElement>, path: string, assemblyId: string) => {
    setIsSwitchMode(false);
    setPendingResolvePath(path);
    pendingAssemblyIdReference.current = assemblyId;
    setCurrentResolvedName(undefined);
    openResolvePopover(event);
  };

  const handleSwitchClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    path: string,
    assemblyId: string,
    resolvedName?: string
  ) => {
    setIsSwitchMode(true);
    setPendingResolvePath(path);
    pendingAssemblyIdReference.current = assemblyId;
    setCurrentResolvedName(resolvedName);
    openResolvePopover(event);
  };

  const handleExistingAssetClick = () => {
    closeResolvePopover();
    setIsAssetSelectionOpen(true);
  };

  const handleUploadAssetClick = (uploadClick: (path: string) => void) => {
    closeResolvePopover();
    uploadClick(pendingResolvePath);
  };

  const handleAssetSelect = (selectedAsset: Asset) => {
    setIsAssetSelectionOpen(false);
    setPendingResolveAsset(selectedAsset);
    setResolveConfirmOpen(true);
  };

  const confirmResolve = () => {
    if (!pendingResolveAsset) return;
    resolveDependency({
      assetId: pendingAssemblyIdReference.current,
      dependencyPath: pendingResolvePath,
      resolvedAssetId: pendingResolveAsset.id,
    });
    setResolveConfirmOpen(false);
    setPendingResolveAsset(undefined);
  };

  const cancelResolve = () => {
    setResolveConfirmOpen(false);
    setPendingResolveAsset(undefined);
  };

  const resolveByUpload = (dependencyPath: string, newAssetId: string) => {
    resolveDependency({
      assetId: pendingAssemblyIdReference.current,
      dependencyPath,
      resolvedAssetId: newAssetId,
    });
  };

  const getConfirmDialogProperties = (pendingDependencyPath: string, pendingFile?: { name: string } | null) => ({
    open: resolveConfirmOpen || false,
    mode: getConfirmDialogMode(resolveConfirmOpen, isSwitchMode),
    dependencyName: getFilename(resolveConfirmOpen ? pendingResolvePath : pendingDependencyPath),
    targetName: resolveConfirmOpen ? (pendingResolveAsset?.path ?? '') : (pendingFile?.name ?? ''),
    currentResolvedName: isSwitchMode ? currentResolvedName : undefined,
    onConfirm: confirmResolve,
    onCancel: cancelResolve,
    onBack: resolveConfirmOpen
      ? () => {
          setResolveConfirmOpen(false);
          setPendingResolveAsset(undefined);
          setIsAssetSelectionOpen(true);
        }
      : undefined,
  });

  return {
    handleResolveClick,
    handleSwitchClick,
    handleExistingAssetClick,
    handleUploadAssetClick,
    handleAssetSelect,
    confirmResolve,
    cancelResolve,
    resolveByUpload,
    isResolvePopoverOpen,
    resolveAnchorElement,
    closeResolvePopover,
    isAssetSelectionOpen,
    setIsAssetSelectionOpen,
    isSwitchMode,
    pendingResolvePath,
    pendingAssemblyId: pendingAssemblyIdReference,
    getConfirmDialogProps: getConfirmDialogProperties,
  };
};

const getConfirmDialogMode = (resolveConfirmOpen: boolean, isSwitchMode: boolean): 'upload' | 'resolve' | 'switch' => {
  if (!resolveConfirmOpen) return 'upload';
  return isSwitchMode ? 'switch' : 'resolve';
};
